import { BCMSCurrency, DisabledPrompt, useModel, useParams } from "@bcm-dev/bcms-vite";
import { availableDestinations } from "components/ui/DestinationSelect";
import { Transaction, Util } from "model";
import React, { useState } from "react";

import { MainContentSection } from "components/shared/containers/MainContentContainer";
import MonthTabsContainer from "components/shared/containers/MonthTabsContainer";
import TransactionView from "components/shared/views/BaseRowView/components/TransactionView";
import { ImageAsset, SVGAsset } from "res";
import { GroupDropdownView } from "../..";

import "./styles.css";

/**
 * @function WhereView
 * @return {React.Component}
 * @exported true
 * @description The main component for WhereView
 * @example
 * <WhereView />
 */
export default function WhereView(props) {
  // Get the card id from the parameter and initalize all transactions and filter by card
  const model = useModel();
  const cardID = useParams().cardID ?? Util.getDefaultCardID(model);
  const transactions = Transaction.initAll().filter(item => item.card.id === cardID, []);
  // Create a new state to control which map should be shown
  const [ zoom, setZoom ] = useState(false);
  const toggle = evt => setZoom(!zoom);
  // Go through all transactions for the specified card and store each transaction
  // based on state and city
  const states = transactions.reduce((result, transaction) => {
    const state = transaction.merchant.state;
    const city = transaction.merchant.city;
    // If the state hasn't been added yet, intialize a new object for it
    if(result[state] === undefined) { result[state] = {}; }
    // If the city hasn't been added yet, initalize a new array for it
    if(result[state][city] === undefined) { result[state][city] = []; }
    // Add the transaction to associated state and city
    
    if(transaction.purchaseLocation !== "Online") {
      result[state][city].push(transaction);
    }

    return result;
  }, {});

  // For each state, sort to make it alphabetical. Then create a new StateGroupDropdown component
  const stateGroups = Object.keys(states)
  .sort((a, b) => {
    if(a > b) { return 1; }
    else if(a < b) { return -1; }
    else{ return 0;}
  })
  .map(key => {
    return <StateGroupDropdown key={ `Group_${ key }` } abbreviation={ key } cities={ states[key] } onClick={ toggle } />
  }, []);

  return (
    <div className="where-view">
      <div className="info-group custom-grid-container">
        <div className="graph-container">
          <DisabledPrompt>
            <div className="map-container chart-asset flex-container align-center-container">
              { !zoom && <ImageAsset id="MapPlaceholderImage" className="zoomed-out-map" data-point="Spend_Insights_Where_Map" /> }
              { zoom && <ImageAsset id="ZoomedInMapImage" className="zoomed-in-map" data-point="Spend_Insights_Where_Map" /> }

              <div className="map-controls-container">
                <DisabledPrompt>
                  <div className="map-control-button clickable zoom-in-button bottom-border">
                    <SVGAsset id="PlusIcon" />
                  </div>
                </DisabledPrompt>
                <DisabledPrompt>
                  <div className="map-control-button clickable zoom-out-button">
                    <SVGAsset id="MinusIcon" />
                  </div>
                </DisabledPrompt>
              </div>

              <div className="map-flag-container flex-container" data-point="Spend_Insights_Flags">
                <DisabledPrompt>
                  <div className="map-flag-button flex-container align-center-container clickable selected">
                    <ImageAsset id="UnitedStatesFlagIcon" className="left-map-icon set-width" />
                    <p className="flex-width">United States</p>
                  </div>
                </DisabledPrompt>
                <DisabledPrompt>
                  <div className="map-flag-button flex-container align-center-container clickable">
                    <ImageAsset id="IndiaFlagIcon" className="left-map-icon set-width" />
                    <p className="flex-width">30</p>
                  </div>
                </DisabledPrompt>
                <DisabledPrompt>
                  <div className="map-flag-button flex-container align-center-container clickable">
                    <ImageAsset id="GermanyFlagIcon" className="left-map-icon set-width" />
                    <p className="flex-width">10</p>
                  </div>
                </DisabledPrompt>
                <DisabledPrompt>
                  <div className="map-flag-button flex-container align-center-container clickable">
                    <ImageAsset id="AustraliaFlagIcon" className="left-map-icon set-width" />
                    <p className="flex-width">10</p>
                  </div>
                </DisabledPrompt>
              </div>
            </div>
          </DisabledPrompt>
        </div>
        <MonthTabsContainer />
      </div>

      <MainContentSection text="Transactions on Map">
        { stateGroups }
      </MainContentSection>
    </div>
  );
}

/**
 * @function StateGroupDropdown
 * @param {STRING} abbreviation - The abbreviation of the state
 * @param {OBJECT} cities - An object of all the cities and the transactions associated
 * @param {FUNCTION} onClick - The toggle to change which map is shown
 * @returns {React.Component}
 * @exported false
 * @description Creates the state dropdown to show which cities transactions where made
 * @example
 * <StateGroupDropdown abbreviation="CA" cities={{}} onClick={ () => {} } />
 */
function StateGroupDropdown(props) {
  // Set the defaults
  const abbreviation = props.abbreviation ?? "";
  const cities = props.cities ?? {};
  const onClick = props.onClick ?? undefined;
  const stateName = availableDestinations().find(item => item.abv === abbreviation && item.isState === true).name;

  // Get all transactions regardless of the city
  const allTransactions = Object.keys(cities).reduce((result, key) => [ ...result, ...cities[key] ], []);
  // Calculate the total amount of all transactions for the state
  const totalAmount = allTransactions.reduce((result, item) => {
    const amount = parseFloat(item.amount.amount);
    return (result + (item.credit === "true" ? -amount : amount));
  }, 0);

  // Create a new state to control if the dropdown contents should be shown or not
  const [ state, setState ] = useState(false);
  const toggle = evt => {
    setState(!state);
    
    if(onClick instanceof Function) {
      onClick();
    }
  };

  // For each city create a new TransactionView item
  const cityItems = Object.keys(cities).map(city => {
    // Get the transactions for the city and calulate the total amount
    const transactions = cities[city];
    //const total = transactions.reduce((result, item) => result + parseFloat(item.amount.amount), 0);
    const total = transactions.reduce((result, item) => {
      const amount = parseFloat(item.amount.amount);
      return (result + (item.credit === "true" ? -amount : amount));
    }, 0);

    return (
      <DisabledPrompt key={ `Transaction_${ city }` }>
        <TransactionView
          billerName={ city }
          date={ `${ transactions.length } Transactions` }
          amount={ BCMSCurrency.toCurrency(total).replace("-", "+") }
          positiveClassName={ total < 0 ? "is-credit" : "" }
          leftIcon={ 
            <p className="set-width align-center-item icon-overlay">
              { transactions.length }
            </p> 
          } />
      </DisabledPrompt>
    );
  }, []);

  // Create the GroupDownView
  const dropdown = (
    <GroupDropdownView
      onClick={ toggle }
      state={ state }
      transactionNumber={ allTransactions.length }
      name={ stateName }
      totalAmount={ BCMSCurrency.toCurrency(totalAmount).replace("-", "+") }
      className={ totalAmount < 0 ? "amount-is-credit" : "" }
      totalNum={ allTransactions.length }>
      
      { cityItems }
    </GroupDropdownView>
  );
  // If the state abbreviation is "CA" then return the dropdown otherwise return the dropdown but disabled
  return abbreviation === "CA" ? dropdown : <DisabledPrompt>{ dropdown }</DisabledPrompt>;
}